import { useEffect, useRef, useState } from 'react';
import type { AlbumFont } from '../../types';
import { fontCss } from '../../utils/albumFonts';
import { Check, ChevronDown, Icon } from '../ui/Icon';

export interface AlbumFontChoice {
  /** null is the box's "the album's". */
  value: string | null;
  label: string;
  font: AlbumFont;
}

/**
 * The album's typeface and a box's, picked from the same list. Hand-rolled
 * instead of a <select>: each option is written in its own font, so the
 * choice is seen, not read — a native option can't take a font on every OS.
 * Open/outside-click/drop-up as in `FilterMenu`.
 */
export default function AlbumFontSelect({ label, choices, value, disabled, onChange }: {
  label: string;
  choices: AlbumFontChoice[];
  value: string | null;
  disabled: boolean;
  onChange: (value: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const current = choices.find(c => c.value === value) ?? choices[0];

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); }
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  function toggle() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropUp(window.innerHeight - rect.bottom < 320);
    }
    setOpen(o => !o);
  }

  function pick(next: string | null) {
    setOpen(false);
    if (next !== value) onChange(next);
  }

  return (
    <div ref={ref} className="relative">
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 ring-1 ring-slate-200 dark:ring-slate-700 hover:ring-teal-300 bg-white dark:bg-slate-800 text-left cursor-pointer disabled:opacity-60"
      >
        <span className="truncate text-base leading-tight text-slate-900 dark:text-slate-100" style={current && fontCss(current.font)}>
          {current?.label}
        </span>
        <Icon as={ChevronDown} size={14} className="shrink-0 text-slate-400" />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={label}
          className={`absolute z-30 left-0 right-0 max-h-72 overflow-y-auto rounded-xl bg-white dark:bg-slate-800 shadow-lg ring-1 ring-slate-200 dark:ring-slate-700 p-1 ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
        >
          {choices.map(c => {
            const on = c.value === value;
            return (
              <li
                key={c.value ?? ''}
                role="option"
                aria-selected={on}
                onClick={() => pick(c.value)}
                className={`flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 cursor-pointer text-base leading-tight ${
                  on ? 'bg-teal-50 text-slate-900 dark:bg-teal-900/30 dark:text-slate-100' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                <span className="truncate" style={fontCss(c.font)}>{c.label}</span>
                {on && <Icon as={Check} size={14} className="shrink-0 text-teal-600" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
